/**
 * Simple error logging service for client applications
 * Logs errors to the error_logs table for monitoring and debugging
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { createClient } from '../client/browser';
import { v4 as uuidv4 } from 'uuid';
import { safeUrlForLog } from '../utils/safe-url';

export interface LogErrorOptions {
  message: string;
  errorType?: string;
  severity?: 'debug' | 'info' | 'warn' | 'error' | 'critical';
  componentName?: string;
  action?: string;
  metadata?: Record<string, any>;
  stack?: string;
  statusCode?: number;
}

class ErrorLoggerService {
  private supabase: SupabaseClient;
  private userId: string | null = null;
  private userRole: string = 'client';

  constructor(supabaseClient?: SupabaseClient) {
    this.supabase = supabaseClient || createClient();
    this.initializeUser();
  }

  private async initializeUser() {
    try {
      // Don't call getUser - just listen for auth changes
      // The onAuthStateChange will fire with INITIAL_SESSION event
      this.supabase.auth.onAuthStateChange((event, session) => {
        this.userId = session?.user?.id || null;
      });
    } catch (error) {
      console.error('[ErrorLogger] Failed to initialize user:', error);
    }
  }

  /**
   * Log an error to the database
   */
  async logError(options: LogErrorOptions): Promise<void> {
    try {
      // Check if Supabase client is properly initialized
      if (!this.supabase || typeof this.supabase.from !== 'function') {
        console.warn('[ErrorLogger] Supabase client not properly initialized, cannot log to database');
        console.log('[ErrorLogger] Error that would have been logged:', options.message);
        return;
      }
      
      // Skip logging in development for non-critical errors
      if (process.env.NODE_ENV === 'development' && options.severity !== 'error' && options.severity !== 'critical') {
        console.log('[ErrorLogger] Skipping DB log in development:', options.message);
        return;
      }

      const errorLog = {
        id: uuidv4(),
        message: options.message.substring(0, 1000),
        errorType: options.errorType || 'GENERAL_ERROR',
        severity: options.severity || 'error',
        userId: this.userId,
        userRole: this.userRole,
        userTimezone: typeof window !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
        url: safeUrlForLog(),
        method: 'CLIENT',
        statusCode: options.statusCode,
        appName: 'client',
        environment: process.env.NODE_ENV || 'production',
        componentName: options.componentName,
        action: options.action,
        errorStack: options.stack?.substring(0, 5000),
        metadata: options.metadata,
        browserName: this.getBrowserName(),
        browserVersion: this.getBrowserVersion()
      };

      const { error } = await this.supabase
        .from('error_logs')
        .insert(errorLog);

      if (error) {
        console.error('[ErrorLogger] Failed to log to database:', error);
      } else {
        console.log('[ErrorLogger] Error logged to database successfully');
      }
    } catch (err) {
      // Never let error logging break the app
      console.error('[ErrorLogger] Critical failure in logError:', err);
    }
  }

  /**
   * Log a simple error message
   */
  async log(message: string, severity: LogErrorOptions['severity'] = 'error'): Promise<void> {
    await this.logError({ message, severity });
  }

  /**
   * Log an error with context
   */
  async logWithContext(
    message: string,
    componentName: string,
    action?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.logError({
      message,
      componentName,
      action,
      metadata,
      severity: 'error'
    });
  }

  /**
   * Log a caught exception
   */
  async logException(
    error: Error | unknown,
    componentName?: string,
    action?: string
  ): Promise<void> {
    if (error instanceof Error) {
      await this.logError({
        message: error.message,
        errorType: error.name,
        stack: error.stack,
        componentName,
        action,
        severity: 'error'
      });
    } else {
      await this.logError({
        message: String(error),
        errorType: 'UNKNOWN_ERROR',
        componentName,
        action,
        severity: 'error'
      });
    }
  }

  /**
   * Log a warning
   */
  async warn(message: string, metadata?: Record<string, any>): Promise<void> {
    await this.logError({
      message,
      severity: 'warn',
      metadata
    });
  }

  /**
   * Log info
   */
  async info(message: string, metadata?: Record<string, any>): Promise<void> {
    await this.logError({
      message,
      severity: 'info',
      metadata
    });
  }

  private getBrowserName(): string | null {
    if (typeof window === 'undefined') return null;
    
    const ua = window.navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  private getBrowserVersion(): string | null {
    if (typeof window === 'undefined') return null;
    
    const ua = window.navigator.userAgent;
    const match = ua.match(/(Chrome|Safari|Firefox|Edge)\/(\d+)/);
    return match ? (match[2] ?? null) : null;
  }
}

// Export singleton instance
let errorLoggerInstance: ErrorLoggerService | null = null;

export function getErrorLogger(supabaseClient?: SupabaseClient): ErrorLoggerService {
  if (!errorLoggerInstance) {
    errorLoggerInstance = new ErrorLoggerService(supabaseClient);
  }
  return errorLoggerInstance;
}

// Export convenient functions
export const errorLogger = {
  log: (message: string, severity?: LogErrorOptions['severity']) => 
    getErrorLogger().log(message, severity),
  
  logError: (options: LogErrorOptions) => 
    getErrorLogger().logError(options),
  
  logException: (error: Error | unknown, componentName?: string, action?: string) =>
    getErrorLogger().logException(error, componentName, action),
  
  logWithContext: (message: string, componentName: string, action?: string, metadata?: Record<string, any>) =>
    getErrorLogger().logWithContext(message, componentName, action, metadata),
  
  warn: (message: string, metadata?: Record<string, any>) =>
    getErrorLogger().warn(message, metadata),
  
  info: (message: string, metadata?: Record<string, any>) =>
    getErrorLogger().info(message, metadata)
};